import { TRANSLATE_SETTINGS_KEY, readStored, writeStored } from './storageKeys';

export const DEFAULT_TRANSLATE_SETTINGS = {
  userLang: 'en',
  remoteLang: 'en',
  domain: 'personal',
  autoDetect: true,
  /** SMS Live Translate: off | ask | always */
  smsMode: 'ask',
};

/** Keep in sync with Live Translate / in-call controls. */
export const TRANSLATE_LANGUAGES = {
  ar: 'Arabic',
  zh: 'Chinese',
  en: 'English',
  fr: 'French',
  de: 'German',
  hi: 'Hindi',
  ja: 'Japanese',
  ko: 'Korean',
  pt: 'Portuguese',
  ru: 'Russian',
  es: 'Spanish',
  vi: 'Vietnamese',
};

export const TRANSLATE_DOMAINS = {
  personal: 'Personal',
  business_casual: 'Business Casual',
  professional: 'Professional',
};

export const SMS_TRANSLATE_MODES = {
  off: 'Off',
  ask: 'Ask per chat',
  always: 'Always on',
};

const SMS_THREAD_MODES_KEY = 'truva_sms_translate_threads';
/** Per-DID translate prefs (208 ≠ 520). Full pair + persona + SMS mode. */
const LINE_TRANSLATE_KEY = 'truva_translate_lines';

function normalizeLineKey(number) {
  const digits = String(number || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return digits.startsWith('+') ? String(number).trim() : `+${digits}`;
}

function loadLineMap() {
  try {
    const raw = readStored(LINE_TRANSLATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function saveLineMap(map) {
  try {
    writeStored(LINE_TRANSLATE_KEY, JSON.stringify(map || {}));
  } catch (_) { /* ignore */ }
}

function coerceBase(merged) {
  if (!TRANSLATE_LANGUAGES[merged.userLang]) merged.userLang = 'en';
  if (!TRANSLATE_LANGUAGES[merged.remoteLang]) merged.remoteLang = DEFAULT_TRANSLATE_SETTINGS.remoteLang;
  if (!TRANSLATE_DOMAINS[merged.domain]) merged.domain = DEFAULT_TRANSLATE_SETTINGS.domain;
  if (!SMS_TRANSLATE_MODES[merged.smsMode]) merged.smsMode = DEFAULT_TRANSLATE_SETTINGS.smsMode;
  merged.autoDetect = merged.autoDetect !== false;
  return merged;
}

function pickTranslateFields(src) {
  return {
    userLang: src.userLang,
    remoteLang: src.remoteLang,
    domain: src.domain,
    autoDetect: src.autoDetect !== false,
    smsMode: src.smsMode,
  };
}

/** Per-line translate prefs when lineNumber is set; else last-used defaults. */
export function loadTranslateSettings(lineNumber) {
  try {
    const raw = readStored(TRANSLATE_SETTINGS_KEY);
    let fallback = { ...DEFAULT_TRANSLATE_SETTINGS };
    if (raw) {
      const parsed = JSON.parse(raw);
      fallback = { ...fallback, ...(parsed && typeof parsed === 'object' ? parsed : {}) };
    }
    coerceBase(fallback);

    const key = normalizeLineKey(lineNumber);
    if (key) {
      const per = loadLineMap()[key];
      if (per && typeof per === 'object') {
        return coerceBase({ ...fallback, ...pickTranslateFields(per) });
      }
    }
    return fallback;
  } catch (_) {
    return { ...DEFAULT_TRANSLATE_SETTINGS };
  }
}

/**
 * Save translate prefs.
 * When lineNumber is set, ALL fields are stored per-line (208 ≠ 520).
 * Global key is only updated as a fallback for brand-new lines / no selection.
 */
export function saveTranslateSettings(settings, lineNumber) {
  try {
    const next = coerceBase({
      ...DEFAULT_TRANSLATE_SETTINGS,
      ...pickTranslateFields(settings || {}),
    });
    const key = normalizeLineKey(lineNumber);
    if (key) {
      const map = loadLineMap();
      map[key] = { ...(map[key] || {}), ...pickTranslateFields(next) };
      saveLineMap(map);
      // Keep a non-line fallback for first paint before a line is chosen.
      writeStored(TRANSLATE_SETTINGS_KEY, JSON.stringify(next));
      return;
    }
    writeStored(TRANSLATE_SETTINGS_KEY, JSON.stringify(next));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** Apply server line-settings payload into local per-line cache. */
export function applyLineTranslateFromServer(lineNumber, payload) {
  const key = normalizeLineKey(lineNumber);
  if (!key || !payload || typeof payload !== 'object') return;
  const map = loadLineMap();
  const row = { ...(map[key] || {}) };
  const userLang = String(payload.user_lang || '').trim().toLowerCase();
  const lang = String(payload.remote_lang || '').trim().toLowerCase();
  const domain = String(payload.domain || '').trim().toLowerCase();
  const sms = String(payload.sms_mode || '').trim().toLowerCase();
  if (TRANSLATE_LANGUAGES[userLang]) row.userLang = userLang;
  if (TRANSLATE_LANGUAGES[lang]) row.remoteLang = lang;
  if (TRANSLATE_DOMAINS[domain]) row.domain = domain;
  if (SMS_TRANSLATE_MODES[sms]) row.smsMode = sms;
  if (typeof payload.auto_detect === 'boolean') row.autoDetect = payload.auto_detect;
  map[key] = row;
  saveLineMap(map);
  // Mirror the server's authoritative pair onto the global (non-line)
  // fallback too, so callers that load translate settings with no line
  // selected (e.g. in-call TranslateControls before a line is chosen)
  // use the line's real languages instead of the English<->Spanish default.
  const updates = {};
  if (TRANSLATE_LANGUAGES[userLang]) updates.userLang = row.userLang;
  if (TRANSLATE_LANGUAGES[lang]) updates.remoteLang = row.remoteLang;
  if (TRANSLATE_DOMAINS[domain]) updates.domain = row.domain;
  if (SMS_TRANSLATE_MODES[sms]) updates.smsMode = row.smsMode;
  if (typeof payload.auto_detect === 'boolean') updates.autoDetect = payload.auto_detect;
  if (Object.keys(updates).length) {
    try {
      const raw = readStored(TRANSLATE_SETTINGS_KEY);
      const fallback = { ...DEFAULT_TRANSLATE_SETTINGS };
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') Object.assign(fallback, parsed);
      }
      Object.assign(fallback, updates);
      writeStored(TRANSLATE_SETTINGS_KEY, JSON.stringify(coerceBase(fallback)));
    } catch (_) { /* ignore quota / private mode */ }
  }
}

export function loadSmsThreadModes() {
  try {
    const raw = readStored(SMS_THREAD_MODES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function saveSmsThreadMode(contact, mode) {
  try {
    const next = { ...loadSmsThreadModes() };
    if (!mode || mode === 'default') delete next[contact];
    else next[contact] = mode;
    writeStored(SMS_THREAD_MODES_KEY, JSON.stringify(next));
    return next;
  } catch (_) {
    return loadSmsThreadModes();
  }
}

export function resolveSmsThreadMode(contact, globalMode = 'ask') {
  const per = loadSmsThreadModes()[contact];
  if (per === 'off' || per === 'ask' || per === 'always') return per;
  return globalMode || 'ask';
}
/**
 * Resolve the effective remote translation language for a party on a call or SMS
 * thread. Priority: saved contact.translate_lang > a language explicitly chosen for
 * this call/screen (manualLang) > the per-line/global setting.
 * Mirrors backend models.contacts_model.resolve_remote_lang. Empty values mean
 * "use global/auto" and are skipped; a manual choice is never silently overwritten
 * (this helper is pure and never writes prefs).
 */
export function resolveRemoteLang(contactLang, manualLang, globalLang) {
  const valid = [contactLang, manualLang, globalLang].find((lang) => lang && TRANSLATE_LANGUAGES[lang]);
  return valid || '';
}
